import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { ChangeSubscriptionModalComponent } from '../change-subscription-modal/change-subscription-modal.component';
import { IconComponent } from '@app/components/shared/icon/icon.component';
import { AuthService } from '@app/services/auth/auth.service';
import { UserStateService } from '@app/services/user-state/user-state.service';
import { CONFIRMATION_MODAL_CONFIGS } from '@app/utils/constants/modal.constants';
import { IconName } from '@app/utils/enums/icon-enum';
import { AppRoutes } from '@app/utils/enums/routes.enum';
import { SubscriptionUnit } from '@app/utils/interfaces/plans.interface';
import { SubscriptionService } from '@app/pages/home-page/shop-settings/settings-subscription/services/subscription/subscription.service';
import { SubPricePipe } from './pipe/sub-price.pipe';

@Component({
  selector: 'app-plans-cards',
  standalone: true,
  imports: [IconComponent, NgClass, TranslateModule, RouterModule, SubPricePipe],
  templateUrl: './plans-cards.component.html',
  styleUrl: './plans-cards.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlansCardsComponent {
  public plans = input<SubscriptionUnit[]>([]);
  public useRouterLink = input(true);
  public cardType = input<'default' | 'subscription'>('default');

  private readonly authService = inject(AuthService);
  private readonly userStateService = inject(UserStateService);
  private readonly dialog = inject(MatDialog);
  private readonly subService = inject(SubscriptionService);

  public iconName = IconName;
  public appRoutes = AppRoutes;

  public activePlan = computed(() => {
    const currentId = this.subService.activePlanId();
    return this.plans().find((p) => p.id === currentId);
  });

  public activePlanId = computed(() => this.activePlan()?.id);

  public popularPlanId = computed(() => {
    const allPlans = this.plans();

    const explicitPopular = allPlans.find((p) => p.isPopular);
    if (explicitPopular) return explicitPopular.id;

    const fallbackPlan = allPlans.find((p) => p.id === 2);
    return fallbackPlan ? fallbackPlan.id : null;
  });

  protected tryChangeSubscription(planId: number): void {
    if (!this.useRouterLink() && this.userStateService.userData()) {
      this.dialog.open(ChangeSubscriptionModalComponent, {
        data: {
          activePlanId: this.activePlanId(),
          newPlanId: planId,
        },
        ...CONFIRMATION_MODAL_CONFIGS,
      });
    }
  }

  public getButtonText(plan: SubscriptionUnit): string {
    const currentActiveId = this.activePlanId();
    const current = this.activePlan();

    if (plan.id === currentActiveId) {
      return 'shop-settings.subscription.plans.plans-general.buttonTexts.current';
    }

    if (!current) {
      return 'shop-settings.subscription.plans.plans-general.buttonTexts.default';
    }

    if (plan.monthlyPrice < current.monthlyPrice) {
      return 'shop-settings.subscription.plans.plans-general.buttonTexts.downgrade';
    }

    if (plan.monthlyPrice > current.monthlyPrice) {
      return 'shop-settings.subscription.plans.plans-general.buttonTexts.upgrade';
    }

    return 'shop-settings.subscription.plans.plans-general.buttonTexts.default';
  }
}
